import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Save, Layers
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const LOCALES = ['en', 'es', 'fr'];
const LOCALE_LABELS = { en: 'EN', es: 'ES', fr: 'FR' };

const PHASE_OPTIONS = ['launch', 'phase_2'];
const PRICING_OPTIONS = ['fixed', 'quote', 'both'];
const MODE_OPTIONS = ['at_client', 'at_location', 'both'];

function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-');
}

// ── Tabbed name/description input ───────────────────────────────────────────
const LocaleInput = ({ label, value, onChange, placeholder }) => {
  const [activeLocale, setActiveLocale] = useState('en');
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="flex gap-1 mb-1">
        {LOCALES.map(loc => (
          <button
            key={loc}
            type="button"
            onClick={() => setActiveLocale(loc)}
            className={`px-3 py-1 text-xs font-semibold rounded border transition-colors
              ${activeLocale === loc ? 'bg-primary text-primary-foreground border-primary' : 'bg-white border-gray-200 text-muted-foreground hover:border-gray-400'}`}
          >
            {LOCALE_LABELS[loc]}
          </button>
        ))}
      </div>
      <Input
        value={value[activeLocale] || ''}
        onChange={e => onChange({ ...value, [activeLocale]: e.target.value })}
        placeholder={placeholder ? `${placeholder} (${activeLocale.toUpperCase()})` : undefined}
      />
    </div>
  );
};

// ── Subcategory row (inline edit) ────────────────────────────────────────────
const SubcategoryRow = ({ sub, onSave, onDelete, onMove, isFirst, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ ...sub });
  const [saving, setSaving] = useState(false);

  const f = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(sub.id, form);
      setExpanded(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg mb-2 overflow-hidden">
      {/* Collapsed header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="flex flex-col gap-0.5" onClick={e => e.stopPropagation()}>
          <button onClick={() => onMove(-1)} disabled={isFirst} className="p-0.5 disabled:opacity-30">
            <ChevronUp className="w-3 h-3" />
          </button>
          <button onClick={() => onMove(1)} disabled={isLast} className="p-0.5 disabled:opacity-30">
            <ChevronDown className="w-3 h-3" />
          </button>
        </div>

        <div className="flex-1 min-w-0">
          <span className="font-medium text-sm">{sub.name?.en || sub.id}</span>
          <span className="ml-2 font-mono text-xs text-muted-foreground">{sub.slug}</span>
        </div>

        <div className="flex items-center gap-2 text-xs" onClick={e => e.stopPropagation()}>
          <Badge variant="outline" className="text-xs">{sub.pricing_model}</Badge>
          <Badge variant="outline" className="text-xs">{sub.service_mode}</Badge>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sub.launch_phase === 'launch' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
            {sub.launch_phase}
          </span>
          <Badge variant={sub.is_active ? 'default' : 'outline'} className="text-xs">
            {sub.is_active ? 'Active' : 'Inactive'}
          </Badge>
          <button
            onClick={() => onDelete(sub.id)}
            className="p-1 text-red-500 hover:text-red-700"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Expanded form */}
      {expanded && (
        <div className="p-4 grid grid-cols-2 gap-4 border-t">
          <div className="col-span-2">
            <LocaleInput
              label="Name"
              value={form.name || {}}
              onChange={v => f('name', v)}
            />
          </div>

          <div>
            <Label>Slug</Label>
            <Input value={form.slug || ''} onChange={e => f('slug', e.target.value)} />
          </div>

          <div>
            <Label>Pricing Model</Label>
            <Select value={form.pricing_model} onValueChange={v => f('pricing_model', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRICING_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Service Mode</Label>
            <Select value={form.service_mode} onValueChange={v => f('service_mode', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Launch Phase</Label>
            <Select value={form.launch_phase} onValueChange={v => f('launch_phase', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PHASE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Platform Fee (e.g. 0.12 = 12%)</Label>
            <Input type="number" step="0.01" value={form.platform_fee_pct ?? 0.12}
              onChange={e => f('platform_fee_pct', parseFloat(e.target.value))} />
          </div>

          <div>
            <Label>Avg Ticket Min (cents)</Label>
            <Input type="number" value={form.avg_ticket_min ?? ''}
              onChange={e => f('avg_ticket_min', e.target.value ? parseInt(e.target.value) : null)} />
          </div>

          <div>
            <Label>Avg Ticket Max (cents)</Label>
            <Input type="number" value={form.avg_ticket_max ?? ''}
              onChange={e => f('avg_ticket_max', e.target.value ? parseInt(e.target.value) : null)} />
          </div>

          {/* Boolean flags */}
          {[
            ['is_urgent', 'Urgent / Emergency'],
            ['is_repeat', 'Recurring service'],
            ['is_seasonal', 'Seasonal demand'],
            ['is_active', 'Active (visible)'],
          ].map(([field, lbl]) => (
            <label key={field} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form[field]}
                onChange={e => f(field, e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm">{lbl}</span>
            </label>
          ))}

          <div className="col-span-2 flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setExpanded(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving}>
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── New subcategory form ─────────────────────────────────────────────────────
const NewSubcategoryForm = ({ categoryId, onCreated, onCancel }) => {
  const [form, setForm] = useState({
    name: { en: '', es: '', fr: '' },
    pricing_model: 'quote',
    service_mode: 'at_client',
    launch_phase: 'phase_2',
    platform_fee_pct: 0.12,
    is_urgent: false,
    is_repeat: false,
    is_seasonal: false,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  const f = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const save = async () => {
    if (!form.name.en) { toast.error('English name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/subcategories`, {
        ...form,
        category_id: categoryId,
        slug: slugify(form.name.en),
      });
      toast.success('Subcategory created');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create subcategory');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-2 border-dashed border-primary/40 rounded-lg p-4 mt-2 grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <LocaleInput label="Name *" value={form.name} onChange={v => f('name', v)} placeholder="Subcategory name" />
      </div>
      <div>
        <Label>Pricing Model</Label>
        <Select value={form.pricing_model} onValueChange={v => f('pricing_model', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {PRICING_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Service Mode</Label>
        <Select value={form.service_mode} onValueChange={v => f('service_mode', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {MODE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Launch Phase</Label>
        <Select value={form.launch_phase} onValueChange={v => f('launch_phase', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {PHASE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Platform Fee (e.g. 0.12)</Label>
        <Input type="number" step="0.01" value={form.platform_fee_pct}
          onChange={e => f('platform_fee_pct', parseFloat(e.target.value))} />
      </div>
      {[['is_urgent', 'Urgent'], ['is_repeat', 'Recurring'], ['is_seasonal', 'Seasonal']].map(([field, lbl]) => (
        <label key={field} className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!form[field]} onChange={e => f(field, e.target.checked)} className="w-4 h-4" />
          <span className="text-sm">{lbl}</span>
        </label>
      ))}
      <div className="col-span-2 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? 'Creating…' : 'Create Subcategory'}
        </Button>
      </div>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const AdminCategoryEditPage = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [catForm, setCatForm] = useState({
    name: { en: '', es: '', fr: '' },
    slug: '',
    icon_slug: '',
    sort_order: '',
    is_active: true,
  });
  const [subcategories, setSubcategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [showNewSub, setShowNewSub] = useState(false);
  const [slugManual, setSlugManual] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [catRes, subRes] = await Promise.all([
        axios.get(`${API}/admin/categories`),
        axios.get(`${API}/admin/subcategories?category_id=${id}`),
      ]);
      const cat = catRes.data.find(c => c.id === id);
      if (!cat) { toast.error('Category not found'); navigate('/admin/categories'); return; }
      setCatForm({
        name: cat.name || { en: '', es: '', fr: '' },
        slug: cat.slug || cat.id,
        icon_slug: cat.icon_slug || '',
        sort_order: cat.sort_order ?? '',
        is_active: cat.is_active !== false,
      });
      setSubcategories(subRes.data);
    } catch {
      toast.error('Failed to load category');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    if (!isNew) fetchData();
  }, [user, navigate, isNew, fetchData]);

  // Auto-generate slug from EN name unless manually edited
  useEffect(() => {
    if (!slugManual && catForm.name.en) {
      setCatForm(prev => ({ ...prev, slug: slugify(prev.name.en) }));
    }
  }, [catForm.name.en, slugManual]);

  const saveCategory = async () => {
    if (!catForm.name.en) { toast.error('English name is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/categories`, catForm);
        toast.success('Category created');
        navigate('/admin/categories');
      } else {
        await axios.put(`${API}/admin/categories/${id}`, catForm);
        toast.success('Category saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const saveSubcategory = async (subId, form) => {
    try {
      await axios.put(`${API}/admin/subcategories/${subId}`, form);
      toast.success('Subcategory saved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save subcategory');
    }
  };

  const deleteSubcategory = async (subId) => {
    if (!window.confirm('Delete this subcategory?')) return;
    try {
      await axios.delete(`${API}/admin/subcategories/${subId}`);
      toast.success('Subcategory deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete subcategory');
    }
  };

  const moveSub = async (index, direction) => {
    const next = [...subcategories];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    setSubcategories(next);
    try {
      await axios.put(`${API}/admin/subcategories/reorder`, { ordered_ids: next.map(s => s.id) });
    } catch {
      toast.error('Failed to save order');
      fetchData();
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/categories')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Categories
          </Button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
            {isNew ? 'New Category' : `Edit: ${catForm.name.en || id}`}
          </h1>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        {/* Category fields */}
        <Card>
          <CardHeader>
            <CardTitle>Category Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <LocaleInput
              label="Name *"
              value={catForm.name}
              onChange={v => setCatForm(p => ({ ...p, name: v }))}
              placeholder="Category name"
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Slug</Label>
                <Input
                  value={catForm.slug}
                  onChange={e => { setSlugManual(true); setCatForm(p => ({ ...p, slug: e.target.value })); }}
                  placeholder="auto-generated"
                />
              </div>
              <div>
                <Label>Icon Slug</Label>
                <Input
                  value={catForm.icon_slug}
                  onChange={e => setCatForm(p => ({ ...p, icon_slug: e.target.value }))}
                  placeholder="e.g. Home, Sparkles, Car"
                />
              </div>
              <div>
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={catForm.sort_order}
                  onChange={e => setCatForm(p => ({ ...p, sort_order: e.target.value ? parseInt(e.target.value) : '' }))}
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={catForm.is_active}
                    onChange={e => setCatForm(p => ({ ...p, is_active: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Active (visible to users)</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={saveCategory} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving…' : 'Save Category'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Subcategories — only shown for existing categories */}
        {!isNew && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Subcategories ({subcategories.length})
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowNewSub(s => !s)}>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Subcategory
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showNewSub && (
                <NewSubcategoryForm
                  categoryId={id}
                  onCreated={() => { setShowNewSub(false); fetchData(); }}
                  onCancel={() => setShowNewSub(false)}
                />
              )}

              {subcategories.length === 0 && !showNewSub ? (
                <div className="py-8 text-center text-muted-foreground">
                  No subcategories yet.
                </div>
              ) : (
                subcategories.map((sub, idx) => (
                  <SubcategoryRow
                    key={sub.id}
                    sub={sub}
                    onSave={saveSubcategory}
                    onDelete={deleteSubcategory}
                    onMove={(dir) => moveSub(idx, dir)}
                    isFirst={idx === 0}
                    isLast={idx === subcategories.length - 1}
                  />
                ))
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminCategoryEditPage;
